import React, { useState } from "react";
import { Box, Button, Collapse, Paper, Typography } from "@mui/material";

// The job's screening questions -- the set the AI interview asks and scores
// against.
//
// ─── WHERE THIS CAME FROM ───────────────────────────────────────────────────
//
// It lived inside CandidateDrawer as an inline component. When the drawer was
// replaced by the full candidate page the questions were left behind, so for a
// short while a job's question set was on screen nowhere at all. Extracted
// here rather than re-typed into the page: it is the only rendering of a
// question set, and it is wanted anywhere a reviewer reads answers.
//
// ─── IT IS READ-ONLY, AND THAT IS DELIBERATE ────────────────────────────────
//
// There is an older ScreeningQuestionsPanel.js in this folder that takes
// `selectedScores`/`onScoreChange` and asks a human to mark every answer out
// of five. Nothing imports it. Marking by hand is what the AI scoring
// replaced -- the per-answer ratings on the interview do that now -- so this
// panel exists to be READ while reviewing: what was asked, what a good answer
// looks like, and what each question is worth.
//
// `expected_answer` is a full model answer with probes and scoring anchors,
// several hundred words of it. Collapsed by default: eleven of those unfurled
// is a wall of text between the reviewer and the actual answers.

const FONT = "'Plus Jakarta Sans', 'Inter', sans-serif";

/**
 * @param {object}  props
 * @param {Array}   props.questions  screening_questions, as the application
 *                                   payload returns them
 * @param {string}  [props.jobTitle] named in the empty state, when known
 */
export default function ScreeningQuestions({ questions, jobTitle }) {
  // Which model answers are open, by question id. Local view state -- nothing
  // outside this panel cares.
  const [open, setOpen] = useState(() => new Set());

  const list = Array.isArray(questions) ? questions : [];

  const toggle = (key) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const allOpen = list.length > 0 && open.size === list.length;

  const toggleAll = () =>
    setOpen(allOpen ? new Set() : new Set(list.map((q, i) => q.id ?? i)));

  if (list.length === 0) {
    return (
      <Box className="cp-card cp-in" sx={{ p: 2.25 }}>
        <Typography className="cp-secname" sx={{ mb: 1.5 }}>
          Screening questions
        </Typography>
        <Typography sx={{ fontFamily: FONT, fontSize: 13, color: "#667085", lineHeight: 1.7 }}>
          No screening questions have been written for
          {jobTitle ? ` ${jobTitle}` : " this job"} yet, so the AI interview
          falls back to questions generated from the role and the
          candidate&rsquo;s resume. Writing your own makes every candidate
          comparable on the same questions.
        </Typography>
      </Box>
    );
  }

  return (
    <Box className="cp-card cp-in" sx={{ p: 2.25 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          mb: 1.5,
        }}
      >
        <Typography className="cp-secname">
          Screening questions
          <Typography
            component="span"
            sx={{ fontFamily: FONT, fontSize: 12, fontWeight: 700, color: "#98a2b3", ml: 0.75 }}
          >
            {list.length}
          </Typography>
        </Typography>

        <Button
          size="small"
          onClick={toggleAll}
          sx={{
            textTransform: "none",
            fontFamily: FONT,
            fontSize: 12.5,
            fontWeight: 700,
            color: "#0b8a8f",
            minWidth: 0,
            px: 0.5,
            "&:hover": { bgcolor: "#ecfdff" },
          }}
        >
          {allOpen ? "Hide all answers" : "Show all answers"}
        </Button>
      </Box>

      {/* Says what the panel is for. A list of questions with no marking UI
          reads as something half-built otherwise. */}
      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          mb: 1.75,
          borderRadius: "10px",
          bgcolor: "#ecfdff",
          border: "1px solid #cff0f2",
        }}
      >
        <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: "#0b6b6f", lineHeight: 1.65 }}>
          The AI asks these {list.length} questions during the interview and
          scores each answer against the rubric below. Nothing needs marking
          here &mdash; read the recorded answers, then decide.
        </Typography>
      </Paper>

      {list.map((q, i) => {
        const key = q.id ?? i;
        const isOpen = open.has(key);
        // The answer options are the scoring anchors for a paragraph question:
        // "an answer like this is worth 2". Shown with the model answer
        // because they are the same reference material.
        const options = Array.isArray(q.answer_options) ? q.answer_options : [];
        const scores = Array.isArray(q.option_scores) ? q.option_scores : [];

        return (
          <Paper
            key={key}
            elevation={0}
            sx={{
              p: 1.75,
              mb: 1,
              borderRadius: "10px",
              bgcolor: "#ffffff",
              border: "1px solid #edf1f7",
            }}
          >
            <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
              <Typography
                sx={{
                  fontFamily: FONT,
                  fontSize: 12,
                  fontWeight: 800,
                  color: "#98a2b3",
                  mt: "2px",
                  minWidth: 16,
                }}
              >
                {i + 1}
              </Typography>

              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    gap: 1.5,
                    alignItems: "baseline",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: FONT,
                      fontSize: 13.5,
                      fontWeight: 700,
                      color: "#101828",
                      lineHeight: 1.6,
                    }}
                  >
                    {q.question}
                  </Typography>

                  {/* What it is worth, read straight off the template row so it
                      matches what the AI scored against. */}
                  {q.max_score != null && Number(q.max_score) > 0 && (
                    <Typography
                      sx={{
                        fontFamily: FONT,
                        fontSize: 11.5,
                        fontWeight: 700,
                        color: "#98a2b3",
                        whiteSpace: "nowrap",
                        flex: "none",
                      }}
                    >
                      {Number(q.max_score)} pts
                    </Typography>
                  )}
                </Box>

                {(q.expected_answer || options.length > 0) && (
                  <>
                    <Button
                      size="small"
                      onClick={() => toggle(key)}
                      sx={{
                        textTransform: "none",
                        fontFamily: FONT,
                        fontSize: 12,
                        fontWeight: 700,
                        color: "#0b8a8f",
                        minWidth: 0,
                        p: 0,
                        mt: 0.75,
                        "&:hover": { bgcolor: "transparent", opacity: 0.75 },
                      }}
                    >
                      {isOpen ? "Hide expected answer" : "Expected answer"}
                    </Button>

                    <Collapse in={isOpen} unmountOnExit>
                      {q.expected_answer && (
                        <Typography
                          sx={{
                            fontFamily: FONT,
                            fontSize: 12.5,
                            color: "#475467",
                            lineHeight: 1.7,
                            mt: 0.9,
                            borderLeft: "2px solid #edf1f7",
                            pl: 1.25,
                            // Model answers arrive with real paragraph breaks;
                            // without this they collapse into one block.
                            whiteSpace: "pre-wrap",
                          }}
                        >
                          {q.expected_answer}
                        </Typography>
                      )}

                      {options.length > 0 && (
                        <Box sx={{ mt: 1.25, pl: 1.25 }}>
                          <Typography
                            sx={{
                              fontFamily: FONT,
                              fontSize: 11,
                              fontWeight: 800,
                              letterSpacing: ".04em",
                              textTransform: "uppercase",
                              color: "#98a2b3",
                              mb: 0.6,
                            }}
                          >
                            Scoring guide
                          </Typography>
                          {options.map((text, oi) => (
                            <Box
                              key={oi}
                              sx={{ display: "flex", gap: 1, alignItems: "flex-start", mb: 0.4 }}
                            >
                              <Typography
                                sx={{
                                  fontFamily: FONT,
                                  fontSize: 11.5,
                                  fontWeight: 800,
                                  color: "#0b8a8f",
                                  bgcolor: "#ecfdff",
                                  border: "1px solid #cff0f2",
                                  borderRadius: "6px",
                                  px: 0.7,
                                  minWidth: 20,
                                  textAlign: "center",
                                  flex: "none",
                                }}
                              >
                                {scores[oi] != null ? Number(scores[oi]) : oi + 1}
                              </Typography>
                              <Typography
                                sx={{
                                  fontFamily: FONT,
                                  fontSize: 12.5,
                                  color: "#475467",
                                  lineHeight: 1.6,
                                }}
                              >
                                {text}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Collapse>
                  </>
                )}
              </Box>
            </Box>
          </Paper>
        );
      })}
    </Box>
  );
}
